import { useCallback, useEffect, useRef, useState } from 'react';
import { showApiError } from 'lib.shared/src/handleApiError';
import {
  THREAD_MESSAGES_PER_PAGE,
  useLazyGetThreadMessagesQuery,
} from 'lib.store/src/type-partner/ai-agent/ai-agent.api';
import type { ThreadMessage } from 'lib.store/src/type-partner/ai-agent/models';

/**
 * How many catch-up pages one call walks before giving up. A catch-up follows a
 * decision or a reconnect, so it is a handful of messages — the cap is there so
 * a thread that grew by thousands while the page was closed cannot spin.
 */
const CATCH_UP_PAGES_MAX = 5;

/**
 * Optimistic bubbles carry Date.now() ids, far above any server id — that is
 * what separates a message the server has never seen from a real one.
 */
const isOptimistic = (message: ThreadMessage): boolean =>
  typeof message.id === 'number' && message.id > 1e12;

/** Local optimistic user message, shown until the server echo arrives */
export const optimisticMessage = (text: string): ThreadMessage =>
  ({
    id: Date.now(),
    parent_id: null,
    message: text,
    is_ai: false,
    agent_type: null,
    role: 'user',
    type: null,
    actions: null,
    artifact: null,
    created_at: new Date().toISOString(),
  }) as unknown as ThreadMessage;

/**
 * Server messages in id order, then whatever the user has sent and the server
 * has not echoed yet.
 *
 * Optimistic ids are clock-based and therefore larger than any server id, so
 * they must NOT be sorted in with the rest: a reply that arrives after a
 * question would otherwise be given a smaller id and render above it.
 */
const order = (settled: ThreadMessage[], pending: ThreadMessage[]): ThreadMessage[] => [
  ...settled.sort((a, b) => Number(a.id) - Number(b.id)),
  ...pending,
];

/**
 * Folds a freshly fetched page into the list already on screen: same-id
 * messages are replaced by the server's copy, everything else is kept — so an
 * older page prepends and a newer one appends without either dropping what the
 * other loaded.
 *
 * A page is also the server's echo of what the user sent, so an optimistic
 * bubble whose text the page carries is dropped here — and ONLY here: dropping
 * it on insert would swallow a message re-sent word for word.
 */
export const mergeMessagePage = (prev: ThreadMessage[], page: ThreadMessage[]): ThreadMessage[] => {
  const byId = new Map<number, ThreadMessage>();
  [...prev, ...page].forEach((message) => byId.set(message.id, message));

  const all = [...byId.values()];
  const settled = all.filter((message) => !isOptimistic(message));
  const pending = all.filter(
    (message) =>
      isOptimistic(message) && !settled.some((s) => !s.is_ai && s.message === message.message),
  );

  return order(settled, pending);
};

/**
 * Adds one message that exists now — a live broadcast, the user's own echo, or
 * an optimistic bubble. Same-id messages are replaced; nothing is dropped by
 * text, so sending the same words twice shows two bubbles.
 */
export const insertMessage = (prev: ThreadMessage[], message: ThreadMessage): ThreadMessage[] => {
  const byId = new Map<number, ThreadMessage>();
  [...prev, message].forEach((item) => byId.set(item.id, item));

  const all = [...byId.values()];

  return order(
    all.filter((item) => !isOptimistic(item)),
    all.filter((item) => isOptimistic(item)),
  );
};

/**
 * The message list of one thread, paged. The ONLY way a surface gets messages:
 * the thread endpoint returns the thread, this returns its conversation.
 *
 * A thread opens on its newest page and grows in two directions: `loadOlder`
 * walks up the history as the user scrolls, `catchUp` pulls only what was said
 * after the newest message already held — which is what a surface calls
 * instead of re-reading the whole thread after it changed something.
 *
 * @param threadId - the open thread, null while it is still resolving
 * @param seed - messages already known without asking (the create-thread
 *        response carries the seeded first message), shown while the first
 *        page is in flight
 */
export const useThreadMessages = (threadId: number | null, seed?: ThreadMessage[]) => {
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [hasOlder, setHasOlder] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [fetchPage, { isFetching: isLoadingOlder }] = useLazyGetThreadMessagesQuery();

  // the callbacks below must see the list as it is now, not as it was when the
  // callback was created
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const apply = useCallback((page: ThreadMessage[]) => {
    const merged = mergeMessagePage(messagesRef.current, page);
    messagesRef.current = merged;
    setMessages(merged);
  }, []);

  const seedRef = useRef(seed);
  seedRef.current = seed;

  // every read and every write is stamped with the thread it was made for: a
  // page (or a send) that resolves after the user opened another conversation
  // must not be merged into it
  const activeThreadRef = useRef(threadId);
  activeThreadRef.current = threadId;

  // opening a thread: reset to what is known without asking, then load the
  // newest page. Keyed on the id alone — a re-render must not refetch
  useEffect(() => {
    const known = seedRef.current ?? [];
    messagesRef.current = known;
    setMessages(known);
    setHasOlder(false);

    if (!threadId) return;

    let cancelled = false;
    setLoading(true);

    fetchPage({ id: threadId, per_page: THREAD_MESSAGES_PER_PAGE })
      .unwrap()
      .then((page) => {
        if (cancelled) return;
        apply(page.data);
        setHasOlder(page.messages_meta.has_more);
      })
      .catch((err) => {
        if (!cancelled) showApiError(err, 'Failed to load the conversation', true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [threadId, fetchPage, apply]);

  /**
   * @param container - the element that scrolls the conversation (pass
   *        `document.documentElement` when the page itself does). A prepended
   *        page grows the list upwards, and browsers without scroll anchoring
   *        (Safari) would leave the viewport on the older messages with the
   *        loader still on screen — which fires the next page immediately, and
   *        the next, walking the whole thread
   */
  const loadOlder = useCallback(
    async (container?: HTMLElement | null) => {
      const oldest = messagesRef.current.find((message) => !isOptimistic(message))?.id;

      if (!threadId || !oldest || isLoadingOlder) return;

      const distanceFromBottom = container ? container.scrollHeight - container.scrollTop : null;

      try {
        const page = await fetchPage({
          id: threadId,
          before_id: oldest,
          per_page: THREAD_MESSAGES_PER_PAGE,
        }).unwrap();

        if (activeThreadRef.current !== threadId) return;

        apply(page.data);
        setHasOlder(page.messages_meta.has_more);

        if (container && distanceFromBottom !== null) {
          // after the prepended rows are laid out, put the reader back where
          // they were reading
          requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight - distanceFromBottom;
          });
        }
      } catch (err) {
        showApiError(err, 'Failed to load earlier messages', true);
      }
    },
    [threadId, isLoadingOlder, fetchPage, apply],
  );

  const catchUp = useCallback(async () => {
    if (!threadId) return;

    try {
      for (let attempt = 0; attempt < CATCH_UP_PAGES_MAX; attempt++) {
        const newest = [...messagesRef.current]
          .reverse()
          .find((message) => !isOptimistic(message))?.id;

        // nothing held yet (the opening page is still in flight): walking
        // FORWARD from zero would return the oldest messages and stop before
        // the tail — the newest page is the catch-up
        if (newest === undefined) {
          const first = await fetchPage({
            id: threadId,
            per_page: THREAD_MESSAGES_PER_PAGE,
          }).unwrap();

          if (activeThreadRef.current !== threadId) return;

          apply(first.data);
          setHasOlder(first.messages_meta.has_more);

          return;
        }

        const page = await fetchPage({
          id: threadId,
          after_id: newest,
          per_page: THREAD_MESSAGES_PER_PAGE,
        }).unwrap();

        if (activeThreadRef.current !== threadId) return;

        if (page.data.length) apply(page.data);

        if (!page.messages_meta.has_more) return;
      }
    } catch (err) {
      showApiError(err, 'Failed to refresh the conversation', true);
    }
  }, [threadId, fetchPage, apply]);

  /**
   * A message that exists now — a live broadcast, or the user's own echo.
   *
   * @param message
   * @param forThread - the thread the message belongs to; the write is dropped
   *        when another conversation has been opened since (a send that
   *        resolves late must not land in the thread now on screen)
   */
  const append = useCallback((message: ThreadMessage, forThread?: number | null) => {
    if (forThread !== undefined && forThread !== activeThreadRef.current) return;

    const next = insertMessage(messagesRef.current, message);
    messagesRef.current = next;
    setMessages(next);
  }, []);

  /**
   * Drops a message that never made it — a failed send's optimistic bubble.
   *
   * @param id
   * @param forThread - see `append`
   */
  const remove = useCallback((id: number, forThread?: number | null) => {
    if (forThread !== undefined && forThread !== activeThreadRef.current) return;

    const next = messagesRef.current.filter((message) => message.id !== id);
    messagesRef.current = next;
    setMessages(next);
  }, []);

  /**
   * What changes when the conversation grows at the BOTTOM and stays put when
   * an older page prepends: the newest server id plus the number of unsent
   * bubbles. Surfaces key their scroll-to-bottom on this instead of on the
   * list length, which an older page also changes.
   */
  const settled = messages.filter((message) => !isOptimistic(message));
  const tailKey = `${settled[settled.length - 1]?.id ?? 0}:${messages.length - settled.length}`;

  return {
    messages,
    append,
    remove,
    isLoading,
    hasOlder,
    isLoadingOlder,
    loadOlder,
    catchUp,
    tailKey,
  };
};
